/**
 * Geodesy helpers for race-course geometry (SPEC §1.2). Coordinates follow the
 * GeoJSON convention throughout: a position is `[lng, lat]` in degrees.
 *
 * Distances are metres. Over a single race course (tens of kilometres, ~46° N)
 * an equirectangular local projection is accurate to well under a metre, so
 * segment maths projects to metres and only the cumulative along-track length
 * uses the haversine formula.
 */

/** `[lng, lat]` degrees. */
export type Position = [number, number] | number[];

/** `[minLng, minLat, maxLng, maxLat]`. */
export type Bbox = [number, number, number, number];

const EARTH_RADIUS_M = 6_371_008.8;
const DEG = Math.PI / 180;

/** Great-circle distance between two `[lng, lat]` positions, in metres. */
export function haversineM(a: Position, b: Position): number {
  const lat1 = a[1] * DEG;
  const lat2 = b[1] * DEG;
  const dLat = lat2 - lat1;
  const dLng = (b[0] - a[0]) * DEG;
  const s =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** Total length of one polyline, in metres. */
export function lineLengthM(line: Position[]): number {
  let total = 0;
  for (let i = 1; i < line.length; i += 1) total += haversineM(line[i - 1]!, line[i]!);
  return total;
}

/** Total length of every polyline of a route, in metres. */
export function totalLengthM(lines: Position[][]): number {
  return lines.reduce((sum, line) => sum + lineLengthM(line), 0);
}

/** Bounding box of every position, or null when there are none. */
export function bboxOf(lines: Position[][]): Bbox | null {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const line of lines) {
    for (const p of line) {
      if (p[0] < minLng) minLng = p[0]!;
      if (p[0] > maxLng) maxLng = p[0]!;
      if (p[1] < minLat) minLat = p[1]!;
      if (p[1] > maxLat) maxLat = p[1]!;
    }
  }
  return Number.isFinite(minLng) ? [minLng, minLat, maxLng, maxLat] : null;
}

/** Grow a bbox by `deg` degrees on every side. */
export function padBbox(b: Bbox, deg: number): Bbox {
  return [b[0] - deg, b[1] - deg, b[2] + deg, b[3] + deg];
}

/** Do two bounding boxes overlap (touching counts)? */
export function bboxIntersects(a: Bbox, b: Bbox): boolean {
  return a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3];
}

/**
 * Local metres-per-degree at a given latitude. Longitude degrees shrink towards
 * the poles; at 46° N one degree of longitude is ~77 km against ~111 km of
 * latitude, so the two axes must be scaled separately.
 */
function metresPerDegree(lat: number): { x: number; y: number } {
  return { x: 111_320 * Math.cos(lat * DEG), y: 110_574 };
}

/**
 * Past this distance a post is not on the course — a car park, a valley
 * position, or a mistyped coordinate. Both sides must agree: the coordinator
 * screens quote a kilometre mark only within it, and the briefing draws the
 * course on a shift card only within it. A split would have Mesta say "ni
 * blizu nobene trase" about the very post whose briefing card shows a km mark.
 */
export const ON_COURSE_MAX_OFFSET_M = 250;

export interface RouteRef {
  /** Index of the polyline the nearest point lies on. */
  lineIndex: number;
  /** Distance from the route's start to the nearest point, in metres. */
  distanceAlongM: number;
  /** Perpendicular distance from the query point to the route, in metres. */
  offsetM: number;
}

/**
 * Nearest point on a multi-part route to `point`, as a distance along the route
 * plus the lateral offset. Powers the "km na trasi" readout on a post and the
 * "this coordinate is nowhere near a course" data-quality warning.
 *
 * `distanceAlongM` accumulates across the parts in order, matching how a runner
 * would cover a course split into several track segments. Returns null for a
 * route with no drawable segment.
 */
export function nearestOnRoute(point: Position, lines: Position[][]): RouteRef | null {
  let best: RouteRef | null = null;
  let travelled = 0;

  for (const [lineIndex, line] of lines.entries()) {
    // Project locally around the query point; over one course the scale is constant.
    const scale = metresPerDegree(point[1]);
    const px = point[0] * scale.x;
    const py = point[1] * scale.y;

    for (let i = 1; i < line.length; i += 1) {
      const a = line[i - 1]!;
      const b = line[i]!;
      const ax = a[0] * scale.x;
      const ay = a[1] * scale.y;
      const bx = b[0] * scale.x;
      const by = b[1] * scale.y;
      const dx = bx - ax;
      const dy = by - ay;
      const lenSq = dx * dx + dy * dy;
      // Fraction along the segment of the perpendicular foot, clamped to its ends.
      const t = lenSq === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
      const offsetM = Math.hypot(px - (ax + t * dx), py - (ay + t * dy));

      if (!best || offsetM < best.offsetM) {
        const segmentM = haversineM(a, b);
        best = { lineIndex, distanceAlongM: travelled + t * segmentM, offsetM };
      }
      travelled += haversineM(a, b);
    }
  }
  return best;
}

/**
 * The stretch of a route within `radiusM` of a point, as a single polyline —
 * the briefing minimap's "which way do the runners come from" context. Returns
 * an empty array when the point is nowhere near the route.
 */
export function clipAround(point: Position, lines: Position[][], radiusM: number): Position[] {
  const ref = nearestOnRoute(point, lines);
  if (!ref) return [];
  const line = lines[ref.lineIndex];
  if (!line || line.length < 2) return [];

  // distanceAlongM counts from the route's start, so rebase onto this part.
  let partStart = 0;
  for (let i = 0; i < ref.lineIndex; i += 1) partStart += lineLengthM(lines[i]!);
  const centre = ref.distanceAlongM - partStart;
  const from = centre - radiusM;
  const to = centre + radiusM;

  // The window's ends are interpolated rather than snapped to whichever
  // vertices happen to survive simplification. After RDP a straight valley
  // stretch can be kilometres between vertices, and keeping only the vertices
  // inside the window would return nothing at all for a post in the middle of
  // one — leaving the briefing card with no course line for a post that is
  // squarely on the course.
  const out: Position[] = [];
  let travelled = 0;
  for (let i = 1; i < line.length; i += 1) {
    const a = line[i - 1]!;
    const b = line[i]!;
    const segment = haversineM(a, b);
    const next = travelled + segment;

    if (segment > 0 && next >= from && travelled <= to) {
      if (travelled < from) out.push(lerp(a, b, (from - travelled) / segment));
      else if (!out.length) out.push(a);

      if (next > to) {
        out.push(lerp(a, b, (to - travelled) / segment));
        break;
      }
      out.push(b);
    }
    travelled = next;
  }
  return out;
}

/** Position a fraction `t` of the way from `a` to `b`. */
function lerp(a: Position, b: Position, t: number): Position {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}
